import isPlainObject from 'lodash/isPlainObject';
import { FolderNotFoundGitError } from '../errors/folder-not-found.git-error';
import { GitFileTreeWithContents } from '../models/git-worker.model';
import { compareSemVer } from '../util/compare-semver';
import { traverseTree } from '../util/traverse-tree';
import { WorkerAction, WorkerFnParams } from '../wasm-git.worker.model';
import { FSAction } from './actions.enum';

interface PathParam {
  path: string;
}

interface AllParams extends PathParam {
  filePathPattern?: string | RegExp;
  fromSchemaVersion?: string;
  toSchemaVersion?: string;
}

/**
 * Reads all files under the given path,
 * whose file names match `fileNameRegex`
 * and whose schema version matches `schemaVersion`
 * and returns their contents.
 */
export interface FSReadFilesAction extends WorkerAction {
  type: FSAction.ReadFiles;
  params: PathParam | AllParams;
}

export async function FSReadFilesActionFn(params: WorkerFnParams<FSReadFilesAction>): Promise<GitFileTreeWithContents> {
  const {
    util,
    fs,
    params: { path, filePathPattern, fromSchemaVersion, toSchemaVersion },
  } = params as Omit<WorkerFnParams<FSReadFilesAction>, 'params'> & { params: AllParams };

  if (!util.itemExistsAt(path)) {
    throw new FolderNotFoundGitError(path);
  }

  const tree = util.readFileTree(path, {
    ignoredNames: ['.gitkeep', 'README.md'],
  }) as GitFileTreeWithContents;

  if (!filePathPattern && !fromSchemaVersion && !toSchemaVersion) {
    return tree;
  }

  traverseTree(tree, (item, _, parent) => {
    if (item.type === 'dir' || !filePathPattern) {
      const children = item.children || {};
      const hasChildren = Object.keys(children).length > 0;

      if (!hasChildren) {
        delete parent[item.name];
      }

      return;
    }

    const match = item.path.match(filePathPattern);

    if (!match) {
      delete parent[item.name];
      return;
    }

    try {
      item.content = fs.readFile(item.path, { encoding: 'utf8' });
    } catch (err) {
      console.debug(`[WasmGit]: could not read file at "${item.path}"`);
    }

    if (item.path.endsWith('.json')) {
      try {
        item.content = JSON.parse((item.content as string | null) || '{}');
      } catch (err) {
        // don't care about error
      }

      if (
        fromSchemaVersion &&
        checkSchemaVersionFormat(fromSchemaVersion) &&
        toSchemaVersion &&
        checkSchemaVersionFormat(toSchemaVersion) &&
        isPlainObject(item.content)
      ) {
        const contentAsObj: { $schema?: unknown } = item.content as { $schema?: unknown };
        const $schema = '$schema' in contentAsObj && typeof contentAsObj.$schema === 'string' && contentAsObj.$schema;

        if (!$schema) {
          return;
        }

        const schemaVersionRegex = /([0-9]+)\.([0-9]+)\.([0-9]+)/;
        const schemaVersion = schemaVersionRegex.exec($schema)?.at(0);

        if (!schemaVersion) {
          return;
        }

        const schemaMatchesFrom = compareSemVer(fromSchemaVersion, schemaVersion) < 1;
        const schemaMatchesTo = compareSemVer(toSchemaVersion, schemaVersion) === 1;

        if (!(schemaMatchesFrom && schemaMatchesTo)) {
          delete parent[item.name];
          return;
        }
      }

      return;
    }
  });

  return tree;
}

/**
 * Check if schemaVersion matches "1.2.3" format
 */
function checkSchemaVersionFormat(schemaVersion: string): boolean {
  const schemaVersionRegexp = /^\d+\.\d+.\d+$/;
  const match = schemaVersion.match(schemaVersionRegexp) !== null;

  if (!match) {
    console.error(`Invalid schema version "${schemaVersion}".`);
  }

  return match;
}
